import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Target, Award, Users, Shield, Home as HomeIcon, ArrowRight, Globe } from 'lucide-react';
import ScheduleModal from '../components/ScheduleModal';

const About = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const stats = [
    { number: '25+', label: 'Years Serving West Volusia' },
    { number: '10,000+', label: 'Patients Served' },
    { number: '2', label: 'Locations in DeLand & Orange City' },
    { number: '100%', label: 'Community Focused' },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-teal-600 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">About Community Medical Center of West Volusia</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            A Rural Health Clinic providing trusted pediatric and internal medicine care to DeLand, Orange City, and West Volusia families since 1998
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 text-white p-3 rounded-lg mr-4">
                  <Target size={32} aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                The Community Medical Center of West Volusia is dedicated to providing accessible, quality pediatric care and internal medicine 
                services to the underserved populations of DeLand, Orange City, Deltona, and West Volusia, Florida. We strive to address 
                the body, mind, and spirit of every patient with compassion and respect, regardless of their social 
                or economic status.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8">
              <div className="flex items-center mb-6">
                <div className="bg-teal-600 text-white p-3 rounded-lg mr-4">
                  <Award size={32} aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                To be the premier healthcare provider in West Volusia County, recognized for our commitment to 
                accessible, high-quality medical care, prevention-focused services, and our dedication to improving 
                the health and wellbeing of every child and adult in our community.
              </p>
            </div>
          </div>

          {/* Rural Health Clinic Section */}
          <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white rounded-2xl p-8 md:p-12 mb-16">
            <div className="flex items-center mb-6">
              <div className="bg-white/20 p-3 rounded-lg mr-4">
                <HomeIcon size={40} aria-hidden="true" />
              </div>
              <h2 className="text-3xl font-bold">Rural Health Clinic Serving DeLand & Orange City</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <p className="text-lg text-blue-50 mb-4 leading-relaxed">
                  As a federally designated <strong>Rural Health Clinic</strong>, we are committed to providing comprehensive pediatric care and internal medicine 
                  to medically underserved rural communities in DeLand, Orange City, Deltona, and throughout West Volusia County. This special designation allows us to serve patients who 
                  might otherwise have difficulty accessing quality healthcare.
                </p>
                <h3 className="font-semibold text-xl mb-3">We Accept Medicaid HMO Plans:</h3>
                <ul className="space-y-2 text-blue-50">
                  <li className="flex items-center"><div className="w-2 h-2 bg-white rounded-full mr-3"></div> Sunshine Health</li>
                  <li className="flex items-center"><div className="w-2 h-2 bg-white rounded-full mr-3"></div> Humana</li>
                  <li className="flex items-center"><div className="w-2 h-2 bg-white rounded-full mr-3"></div> Florida Community Care</li>
                  <li className="flex items-center"><div className="w-2 h-2 bg-white rounded-full mr-3"></div> UnitedHealthcare</li>
                </ul>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="font-semibold text-xl mb-4">Our Commitment to Access</h3>
                <ul className="space-y-3 text-blue-50">
                  <li className="flex items-start">
                    <Shield className="mr-3 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <span>Sliding fee scale for uninsured patients based on family size and income</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="mr-3 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <span>Walk-ins welcome at both DeLand and Orange City locations</span>
                  </li>
                  <li className="flex items-start">
                    <Heart className="mr-3 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <span>Compassionate care for the whole person - body, mind, and spirit</span>
                  </li>
                  <li className="flex items-start">
                    <Globe className="mr-3 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <span>Bilingual staff - Se Habla Español</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Story: Serving West Volusia Since 1998</h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Community Medical Center of West Volusia was established in 1998 with a simple yet powerful mission: 
                  to ensure that quality healthcare is accessible to everyone in our community, regardless of their 
                  ability to pay.
                </p>
                <p>
                  Over the past two decades, we have grown from a small clinic to a comprehensive healthcare center 
                  serving thousands of patients across DeLand, Orange City, Deltona, and West Volusia County. Our commitment to preventive care, chronic disease 
                  management, and patient education has helped improve health outcomes for countless families.
                </p>
                <p>
                  Today, with two convenient locations in <strong>Orange City</strong> and <strong>DeLand</strong>, we continue to expand our services 
                  to meet the evolving needs of our community. Our dedicated 
                  team—including <Link to="/providers" className="text-blue-600 hover:underline">Dr. Concepcion Anayas, M.D. (Pediatrics), Dr. Marcelo Anayas, M.D. (Internal Medicine), and Pam Perry, ARNP (Pediatrics)</Link>—works tirelessly to ensure every patient receives compassionate, 
                  quality care.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/providers" className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700">
                  Meet Our Providers
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </div>
            </div>
            <div className="space-y-4">
              <img 
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2MDU4NzU0OHww&ixlib=rb-4.1.0&q=85" 
                alt="Healthcare professional providing care in West Volusia Florida" 
                className="rounded-2xl shadow-xl w-full h-64 object-cover"
              />
              <img 
                src="https://images.pexels.com/photos/4021775/pexels-photo-4021775.jpeg" 
                alt="Medical team at Community Medical Center serving DeLand and Orange City families" 
                className="rounded-2xl shadow-xl w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bilingual Staff Section */}
      <section className="py-16 px-4 bg-teal-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Globe size={32} />
            <h2 className="text-3xl font-bold">Se Habla Español</h2>
          </div>
          <p className="text-lg text-teal-100">
            Our clinic has bilingual staff members who speak Spanish to better serve our diverse West Volusia community. 
            We are committed to providing excellent care to all patients regardless of language preference.
          </p>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-teal-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Our Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Heart size={32} />,
                title: 'Compassion',
                description: 'We treat every patient with empathy, dignity, and genuine care.',
              },
              {
                icon: <Shield size={32} />,
                title: 'Integrity',
                description: 'We uphold the highest ethical standards in all our interactions with West Volusia families.',
              },
              {
                icon: <Users size={32} />,
                title: 'Community',
                description: 'We are dedicated to serving and improving our DeLand and Orange City community.',
              },
              {
                icon: <Award size={32} />,
                title: 'Excellence',
                description: 'We strive for excellence in every aspect of patient care.',
              },
            ].map((value, index) => (
              <div key={index} data-testid={`core-value-${index}`} className="text-center bg-white rounded-xl p-6 shadow-md flex flex-col items-center h-full">
                <div className="bg-gradient-to-br from-blue-600 to-teal-600 text-white w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 flex-shrink-0">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600 flex-grow">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Ready to Experience Compassionate Care?</h2>
          <p className="text-xl mb-10 text-blue-200">
            Visit our clinics in DeLand and Orange City. Medicaid accepted, walk-ins welcome, bilingual staff available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowScheduleModal(true)}
              data-testid="about-schedule-btn"
              className="inline-flex items-center justify-center bg-white text-blue-700 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-50 transition-all cursor-pointer"
            >
              Schedule Appointment
            </button>
            <a
              href="tel:386-775-1792"
              data-testid="about-call-btn"
              className="inline-flex items-center justify-center bg-blue-800 border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-900 transition-all"
            >
              Call (386) 775-1792
            </a>
          </div>
        </div>
      </section>

      <ScheduleModal isOpen={showScheduleModal} onClose={() => setShowScheduleModal(false)} />
    </div>
  );
};

export default About;
